import axiosInstance from '../axios/axios-instance';
import { AxiosResponse } from 'axios';
import { EmailSendResult } from 'shared/enums/email/email-send-result.enum';
import { EmailTemplate } from 'shared/enums/email/email-template.enum';
import { ChangePasswordResponse } from 'shared/interfaces/auth/change-password-response-interface';
import { EmailVerificationStatusResponse } from 'shared/interfaces/auth/email-verification-status-response.interface';
import { LoginFlowResult } from 'shared/interfaces/auth/login-flow-result.interface';
import { LoginResponse } from 'shared/interfaces/auth/login-response.interface';
import { RefreshTokenResponse } from 'shared/interfaces/auth/refresh-token-response.interface';
import { RequestEmailChangeResponse } from 'shared/interfaces/auth/request-email-change-response.interface';
import { TokenVerificationResponse } from 'shared/interfaces/auth/token-verification-response.interface';

let activeRefreshRequest: Promise<AxiosResponse<RefreshTokenResponse>> | null = null;

export const auth = {
  /**
   * Registers a new user using their email and password.
   *
   * @param email - The email address of the user
   * @param password - The password for the new account
   * @param isConsentChecked - Whether the user has accepted TOS and privacy policy
   * @param referralCode - Optional referral code from affiliate link
   * @param utmParams - UTM parameters for tracking user acquisition
   * Sends the verification email. Authentication happens after the user verifies their email.
   */
  async register(
    email: string,
    password: string,
    isConsentChecked: boolean,
    referralCode?: string,
    utmParams?: {
      utm_source?: string;
      utm_medium?: string;
      utm_campaign?: string;
      utm_term?: string;
      utm_content?: string;
      utm_id?: string;
      utm_affclickid?: string;
    },
    verisoulSessionId?: string
  ): Promise<void> {
    await axiosInstance.post<void>(
      '/auth/register',
      {
        email,
        password,
        isConsentChecked,
        referralCode,
        ...utmParams,
      },
      {
        headers: {
          'user-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
          'x-session-id': verisoulSessionId,
        },
      }
    );
  },

  /**
   * Logs in an existing user using email and password.
   *
   * @param email - The user's email address
   * @param password - The user's password
   * @returns The login response
   */
  async login(email: string, password: string, verisoulSessionId?: string): Promise<LoginResponse> {
    const response = await axiosInstance.post<LoginResponse>(
      '/auth/login',
      { email, password },
      {
        withCredentials: true,
        headers: {
          'user-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
          'x-session-id': verisoulSessionId,
        },
      }
    );
    return response.data;
  },

  /**
   * Refreshes the user's access token using the stored refresh token.
   *
   * @returns The access token
   */
  async refreshAccessToken(reinitializeSession?: () => Promise<void>): Promise<RefreshTokenResponse> {
    activeRefreshRequest ??= axiosInstance
      .post<RefreshTokenResponse>(
        '/auth/refresh-access-token',
        {}, // No body required, refresh token sent via cookie
        { withCredentials: true, headers: { 'user-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone } } // Ensures the refresh token is sent with the request
      )
      .finally(() => {
        activeRefreshRequest = null;
      });

    const response = await activeRefreshRequest;
    if (response.headers['x-ip-changed']) {
      await reinitializeSession?.();
    }
    return response.data;
  },

  /**
   * Logs out the user by invalidating the refresh token on the server.
   */
  async logout(): Promise<void> {
    await axiosInstance.post('/auth/logout', {}, { withCredentials: true });
  },

  /**
   * Sends a reset password request for a user, initiating the reset password flow.
   *
   * @param email - The user's email address for reset password
   */
  async resetPasswordRequest(email: string, verisoulSessionId?: string): Promise<EmailSendResult> {
    const response = await axiosInstance.post<EmailSendResult>(
      '/auth/reset-password-request',
      { email },
      {
        headers: {
          'x-session-id': verisoulSessionId,
        },
      }
    );
    return response.data;
  },

  /**
   * Resets the user's password using a reset token and the new password.
   *
   * @param token - The reset password token received by the user
   * @param newPassword - The new password to set for the user
   * @returns The user email. It is used to automatically log in the user after resetting the password.
   */
  async resetPassword(token: string, newPassword: string): Promise<string> {
    const response = await axiosInstance.post<string>('/auth/reset-password', { token, newPassword });
    return response.data;
  },

  /**
   * Changes the user's password and deactivates all other sessions.
   * The current session remains active.
   *
   * @param oldPassword - The user's old password
   * @param newPassword - The user's new password
   * @returns The access token and refresh token
   */
  async changePassword(
    oldPassword: string,
    newPassword: string,
    verisoulSessionId?: string
  ): Promise<ChangePasswordResponse> {
    const response = await axiosInstance.post<ChangePasswordResponse>(
      '/auth/change-password',
      { oldPassword, newPassword },
      { withCredentials: true, headers: { 'x-session-id': verisoulSessionId } }
    );
    return response.data;
  },

  /**
   * Sends a request to change the user's email.
   *
   * @param newEmail - The user's new email address
   * @returns The email send result
   */
  async emailChangeRequest(newEmail: string, verisoulSessionId?: string): Promise<RequestEmailChangeResponse> {
    const response = await axiosInstance.post<RequestEmailChangeResponse>(
      '/auth/email-change-request',
      { newEmail },
      { withCredentials: true, headers: { 'x-session-id': verisoulSessionId } }
    );
    return response.data;
  },

  /**
   * Verifies the email change using a token provided via a query parameter.
   *
   * @param token - The email change verification token
   * @returns A response object indicating the result of the verification process
   */
  async verifyEmailChangeToken(token: string): Promise<TokenVerificationResponse> {
    const response = await axiosInstance.post<TokenVerificationResponse>('/auth/verify-email-change-token', { token });
    return response.data;
  },

  /**
   * Verifies the user's email using a token provided via a query parameter.
   *
   * @param token - The email verification token
   * @returns A response object indicating the result of the verification process
   */
  async verifyEmail(token: string): Promise<TokenVerificationResponse> {
    const response = await axiosInstance.post<TokenVerificationResponse>('/auth/verify-email', { token });
    return response.data;
  },

  /**
   * Sends a request to the backend to determine if the email is valid and also what is the login flow that
   *  we should show to the user - registration, login, google sso login.
   *
   * @param email - The user's email address
   * @returns The email status based on which we decide what to show the user next - error message, register/login screen
   */
  // TODO: This method needs more security as part of PROD-96
  async determineLoginFlow(email: string): Promise<LoginFlowResult> {
    const response = await axiosInstance.post<LoginFlowResult>('/auth/determine-login-flow', { email });
    return response.data;
  },

  /**
   * Creates a new user account when user uses Google SSO.
   * Called after the user accepts TOS and privacy policy.
   * Pass referralCode when the user landed via an affiliate link so the referrer gets credit.
   *
   * @returns The access token
   */
  async createGoogleSsoAccount(
    isConsentChecked: boolean,
    verisoulSessionId?: string,
    referralCode?: string
  ): Promise<string> {
    const response: AxiosResponse<RefreshTokenResponse> = await axiosInstance.post(
      '/auth/google/create-account',
      { isConsentChecked, referralCode: referralCode || undefined },
      {
        withCredentials: true,
        headers: {
          'user-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
          'x-session-id': verisoulSessionId,
        },
      } // Ensures the refresh token is sent with the request
    );
    return response.data.accessToken;
  },

  /**
   *
   * @param email The email address of the user
   * @returns Whether or not the email was sent or the maximum resends limit was reached
   */
  async resendVerificationEmail(email: string): Promise<EmailSendResult> {
    const response = await axiosInstance.post<EmailSendResult>('/auth/resend-verification-email', { email });
    return response.data;
  },

  /**
   *
   * Get a count of how many times the user has executed the "resend email" action.
   *
   * @param email The email to check
   * @param emailTemplate The template of email to check for
   */
  async getEmailCount(email: string, emailTemplate: EmailTemplate): Promise<number> {
    const response = await axiosInstance.get<number>('/auth/email-count', {
      params: {
        email,
        emailTemplate,
      },
    });
    return response.data;
  },

  async getEmailVerificationStatus(email: string): Promise<EmailVerificationStatusResponse> {
    const response = await axiosInstance.post<EmailVerificationStatusResponse>('/auth/email-verification-status', {
      email,
    });
    return response.data;
  },

  /**
   * Verifies whether the provided token is a valid reset password token.
   *
   * @param token The token to verify
   * @returns A boolean indicating whether the token is valid
   */
  async verifyResetPasswordToken(token: string): Promise<TokenVerificationResponse> {
    const response = await axiosInstance.post<TokenVerificationResponse>('/auth/verify-reset-password-token', {
      token,
    });
    return response.data;
  },
};
